import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Close, HowToReg, Warning } from '@mui/icons-material';
import { merchantManagementAPI } from '../../services/api';
import { formatCredits } from '../../utils/numberFormat';

// Account verification: free tokens are granted only after an admin approves the merchant.
export const VERIFICATION_LABELS = {
  pending: 'Pending Verification',
  approved: 'Approved',
  rejected: 'Rejected',
};

const VERIFICATION_CHIP_SX = {
  pending: { bgcolor: '#FEF3C7', color: '#d97706' },
  approved: { bgcolor: '#F0FDF4', color: '#10B981' },
  rejected: { bgcolor: '#FEE2E2', color: '#D32F2F' },
};

export const VerificationChip = ({ status, size = 'small', sx = {} }) => {
  if (!status) return null;
  return (
    <Chip
      label={VERIFICATION_LABELS[status] || status}
      size={size}
      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, ...VERIFICATION_CHIP_SX[status], ...sx }}
    />
  );
};

const HISTORY = {
  verification_submitted: { label: 'Submitted for verification', color: '#d97706' },
  verification_approved: { label: 'Approved', color: '#10B981' },
  verification_rejected: { label: 'Rejected', color: '#D32F2F' },
};

// Timestamps without an offset are stored in UTC — show everything in IST.
export const formatDateTimeIST = (value) => {
  if (!value) return '-';
  const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);
  const date = new Date(hasZone ? value : `${value}Z`);
  if (isNaN(date.getTime())) return '-';
  return `${date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  })} IST`;
};

const Field = ({ label, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
      {label}
    </Typography>
    <Typography component="div" variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
      {children || '-'}
    </Typography>
  </Box>
);

const Section = ({ title, children }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%', borderRadius: 2 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
      {title}
    </Typography>
    <Box sx={{ display: 'grid', gap: 1.5 }}>{children}</Box>
  </Paper>
);

const YesNo = ({ value }) => (
  <Chip
    label={value ? 'Verified' : 'Not verified'}
    size="small"
    sx={{
      height: 20,
      fontSize: '0.7rem',
      fontWeight: 600,
      ...(value ? { bgcolor: '#F0FDF4', color: '#10B981' } : { bgcolor: '#F3F4F6', color: '#6B7280' }),
    }}
  />
);

const SummaryTile = ({ label, value, hint }) => (
  <Box sx={{ flex: '1 1 140px', minWidth: 0, px: 2, py: 1.25, bgcolor: '#F9FAFB', borderRadius: 2 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{value}</Typography>
    {hint && <Typography variant="caption" sx={{ color: '#651BC6', fontWeight: 600 }}>{hint}</Typography>}
  </Box>
);

const phone = (code, number) => (number ? `${code || ''} ${number}`.trim() : null);

const MerchantVerificationDialog = ({ open, merchant, canVerify, onClose, onDone }) => {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState('');  // '' | 'approve' | 'reject'
  const [error, setError] = useState('');
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open || !merchant) return;
    setReview(null);
    setError('');
    setNotes('');
    setReason('');
    setLoading(true);
    merchantManagementAPI.getVerification(merchant.id)
      .then((res) => setReview(res.data))
      .catch((err) => setError(err.response?.data?.error || 'Failed to load verification details'))
      .finally(() => setLoading(false));
  }, [open, merchant]);

  if (!merchant) return null;

  const verification = review?.verification || {};
  const status = verification.verification_status || merchant.verification_status;
  const canDecide = canVerify && (status === 'pending' || status === 'rejected');
  const offer = review?.free_tokens_offer || 0;
  const related = review?.related_accounts || [];
  const history = review?.history || [];

  const submit = async (decision) => {
    setSubmitting(decision);
    setError('');
    try {
      const res = decision === 'approve'
        ? await merchantManagementAPI.approveVerification(merchant.id, { notes })
        : await merchantManagementAPI.rejectVerification(merchant.id, { reason, notes });
      onDone(res.data.message);
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${decision} merchant`);
    } finally {
      setSubmitting('');
    }
  };

  const plan = review?.current_plan;

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
        <Avatar sx={{ bgcolor: '#651BC6', width: 44, height: 44, fontWeight: 700 }}>
          {merchant.name?.charAt(0)?.toUpperCase() || 'M'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
              {merchant.name}
            </Typography>
            <VerificationChip status={status} />
          </Box>
          <Typography variant="body2" color="text.secondary" noWrap>
            Verify for free tokens · ID {merchant.id}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={!!submitting} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: '#FCFCFD' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
              <SummaryTile label="Source" value={merchant.registration_source || '-'} />
              <SummaryTile label="Current plan" value={plan ? `${plan.plan_name} (${plan.status})` : 'No active plan'} />
              <SummaryTile
                label="Token balance"
                value={review ? formatCredits(review.token_balance || 0) : '-'}
                hint={canDecide && offer > 0 ? `+${formatCredits(offer)} on approval` : null}
              />
            </Box>

            {related.length > 0 && (
              <Alert severity="warning" icon={<Warning />} sx={{ mb: 2, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {related.length} other account{related.length > 1 ? 's' : ''} share{related.length > 1 ? '' : 's'} this merchant's details
                </Typography>
                {related.map((acc) => (
                  <Typography key={acc.id} variant="body2">
                    <strong>{acc.name}</strong> ({acc.email || 'no email'}) — same {acc.matched_on.join(', ')}
                    {' · '}{VERIFICATION_LABELS[acc.verification_status] || acc.verification_status}
                    {acc.free_tokens_granted ? ' · already received free tokens' : ''}
                    {acc.is_deleted ? ' · deleted' : ''}
                  </Typography>
                ))}
              </Alert>
            )}

            {status === 'rejected' && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                Rejected by {verification.verification_reviewed_by_name || 'admin'} on{' '}
                {formatDateTimeIST(verification.verification_reviewed_at)} — {verification.verification_rejection_reason}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Section title="Contact">
                  <Field label="Email">
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <span>{merchant.email || '-'}</span>
                      <YesNo value={merchant.email_verified} />
                    </Box>
                  </Field>
                  <Field label="Mobile">
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <span>{phone(merchant.mobile_code, merchant.mobile) || '-'}</span>
                      <YesNo value={merchant.mobile_verified} />
                    </Box>
                  </Field>
                  <Field label="WhatsApp">{phone(merchant.whatsapp_code, merchant.whatsapp_number)}</Field>
                </Section>
              </Grid>
              <Grid item xs={12} md={6}>
                <Section title="Business & activity">
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    <Field label="Company">{merchant.company_name}</Field>
                    <Field label="GST">{merchant.gst_number}</Field>
                    <Field label="Address">{[merchant.address, merchant.pincode].filter(Boolean).join(', ')}</Field>
                    <Field label="Account">{merchant.is_suspended ? 'Suspended' : merchant.is_active ? 'Active' : 'Inactive'}</Field>
                    <Field label="Registered">{formatDateTimeIST(merchant.created_at)}</Field>
                    <Field label="Last login">{formatDateTimeIST(merchant.last_login_at)}</Field>
                  </Box>
                </Section>
              </Grid>
            </Grid>

            {history.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>History</Typography>
                {history.map((h, idx) => {
                  const meta = HISTORY[h.action] || { label: h.action, color: '#6B7280' };
                  const details = [
                    h.actor_name && `by ${h.actor_name}`,
                    h.new_values?.free_tokens_granted ? `${formatCredits(h.new_values.free_tokens_granted)} free tokens added` : null,
                    h.new_values?.reason && `Reason: ${h.new_values.reason}`,
                    h.notes && `Note: ${h.notes}`,
                  ].filter(Boolean).join(' · ');
                  return (
                    <Box key={idx} sx={{ display: 'flex', gap: 1.5, pb: idx === history.length - 1 ? 0 : 1.5 }}>
                      <Box sx={{ width: 10, height: 10, mt: '6px', borderRadius: '50%', bgcolor: meta.color, flexShrink: 0 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{meta.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTimeIST(h.created_at)}{details ? ` · ${details}` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Paper>
            )}

            {canDecide && (
              <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Decision</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={status === 'pending' ? 6 : 12}>
                    <TextField
                      label="Internal notes (optional)"
                      helperText="Visible to admins only"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      fullWidth multiline minRows={3} size="small"
                    />
                  </Grid>
                  {status === 'pending' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Rejection reason"
                        helperText={reason.trim()
                          ? `Clear the reason to approve instead · admins only (${reason.length}/500)`
                          : `Required to reject · admins only, not sent to the merchant (${reason.length}/500)`}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        inputProps={{ maxLength: 500 }}
                        fullWidth multiline minRows={3} size="small"
                      />
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={!!submitting} sx={{ mr: 'auto' }}>Close</Button>
        {canDecide && status === 'pending' && (
          <Button
            color="error"
            variant="outlined"
            disabled={!!submitting || loading || !reason.trim()}
            startIcon={submitting === 'reject' ? <CircularProgress size={16} color="inherit" /> : null}
            onClick={() => submit('reject')}
          >
            Reject
          </Button>
        )}
        {canDecide && (
          <Button
            color="success"
            variant="contained"
            disabled={!!submitting || loading || !!reason.trim()}
            startIcon={submitting === 'approve' ? <CircularProgress size={16} color="inherit" /> : <HowToReg />}
            onClick={() => submit('approve')}
          >
            {offer > 0 ? `Approve & add ${formatCredits(offer)} free tokens` : 'Approve'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MerchantVerificationDialog;
